'use strict';

import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader';
import {
    addAmenitiesWithoutEngineId,
    createMaterialAndSetBaseColor, filterDisabledLanguages, isOneMesh,
    lazyLoadingData,
} from '../../functions';

import { Group, Vector3 } from 'three';
import { settingsData, threeJsVariables, isMobile, Buildings, AVAILABLE_STATUS, projectMode } from '../../settings';
import {BuildingLabels} from "../components/building-switch/buildingLabels";
import {SelectedObjectType} from "../../common";
import {FBXLoader} from 'three/examples/jsm/loaders/FBXLoader';
import { calculateBoxForSelectionBoxes } from '../components/building-switch/buildingHover';
import { setCoordinates } from './autoProjectScript';
import { Vantages } from '../components/vantage/Vantage';
import { createTooltip } from '../components/flatTooltip/flatTooltip';
import { BuildingBBOX } from '../building/Building';
import { CameraCollision } from './CameraCollision';
import {sendLogs} from "../crm/crm-requests";

let _hightlights = null;

export function loadHighlightsModelFromAzure(response, dracoLoader) {
    const [buildingInfo] = response;
    const {cdnRepository, projectFolder} = config;
    const {format} = config.resources.buildings;
    const {folder} = config.resources.selectionBoxes;
    let loader;

    const modelsPromiseArray = [];

    switch (format) {
        case 'gltf':
            loader = new GLTFLoader();
            loader.setDRACOLoader(dracoLoader);
            break;
        case 'fbx':
            loader = new FBXLoader();
            break;

    }

    buildingInfo.data.buildings.forEach((building, index) => {
        const url = `${cdnRepository}/${projectFolder}/${folder}/${building.buildingEngineId}_selectBoxes.${format}`;
        const model = lazyLoadingData(loader, url);

        modelsPromiseArray.push(model);
    });

    return modelsPromiseArray;
}


export function bundle_initAutoHighlightsScript(response) {
    const crmData = response.filter(el => Array.isArray(el))[0];
    const [crmProps,crmSettings] = crmData;
    const activeBuildings = [];
    const highlightsData = response.filter(el => !Array.isArray(el));
    crmSettings.data.buildings.forEach(building => {
       if (!Buildings.isUsedBuilding(building.buildingEngineId))
           return;

       const selections = highlightsData.find(selection => selection.name.startsWith(building.buildingEngineId));
       if (selections) {
           activeBuildings.push(selections);
       } else {
           crmProps.data = crmProps.data.filter(prop => prop.buildingEngineId !== building.buildingEngineId);
       }
    });

    settingsData.allCrmData = crmProps.data;

    if (highlightsData.length === 0) {
        crmSettings.data.buildings = [];
        activeBuildings.push(new Group());

        return;
    }

    concatPropsAndHighlights(crmProps, activeBuildings, crmSettings);
    addHighlightsToScene(activeBuildings);

    BuildingLabels(activeBuildings);

    if (!isMobile)
        createTooltip();
}


export function initAutoHighlightsScript(response) {
    const crmData = response.filter(el => Array.isArray(el))[0];
    const [crmSettings, crmProps, dictionary] = crmData;
    const highlightsData = response.filter(el => !Array.isArray(el));

    settingsData.allCrmData = crmProps.data;

    const highlights = getHighlightsFromResponse(highlightsData);
    concatPropsAndHighlights(crmProps, highlights, crmSettings);
    addHighlightsToScene(highlights);
    setProjectSettings(crmSettings, dictionary);

    BuildingLabels(highlights);

}

function addHighlightsToScene(highlights) {
    const {projectRotation} = config.threejs;
    const projectHighlightsGroup = new Group();
    if (!threeJsVariables.bundleConfig) {
        projectHighlightsGroup.rotation.set(projectRotation.x, projectRotation.y, projectRotation.z);
    } else if (threeJsVariables.bundleConfig && threeJsVariables.bundleConfig.version) {
        setCoordinates(projectHighlightsGroup,threeJsVariables.bundleConfig.project);
    } else {
        setCoordinates(projectHighlightsGroup,threeJsVariables.bundleConfig.building);
    }
    projectHighlightsGroup.name = 'Project Selection Boxes';
    projectHighlightsGroup.add(...highlights);
    calculateBoxForSelectionBoxes(highlights);
    const cameraCollision = new CameraCollision();
    cameraCollision.addObject(projectHighlightsGroup);

    // fitModelAndSetMaxMinProperties();

    _hightlights = projectHighlightsGroup
}

export const getHighlights = () => _hightlights;

export const getSelectionsByName = (name) => _hightlights.getObjectByName(name + '_selectionBoxes');

export function setHighlights (highlights) {
    _hightlights = highlights;
}

function getHighlightsFromResponse(highlights) {
    const {format} = config.resources.buildings;
    const highlightsArr = [];

    switch (format) {
        case 'gltf':
            highlights.forEach(el => {
                const sBoxBuilding = el.scene.children[0];
                highlightsArr.push(sBoxBuilding);
            });
            break;
        case 'fbx':
            highlights.forEach(el => highlightsArr.push(el));
            break;

    }

    return highlightsArr;
}

export function concatPropsAndHighlights(props, highlights, settings) {
    const dataProps = props.data;

    highlights.forEach(building => {
        if (isOneMesh() && !projectMode.AirMode.isSchematicBuildings()) {
            const bboxBuilding = new BuildingBBOX(building).create();
            bboxBuilding.updateAttachedModelsList();
            // bboxBuilding.hideSceneObjectsOfCurrentBuilding();
        }

        const buildingName = building.name.split('_')[0];
        const buildingClass = Buildings.getBuilding(buildingName);
        const info = createBuildingInfo(building, dataProps, settings);
        //building.children = sortFloors(building);
        building.children = building.children.sort((prev, next) => {
            let n1 = parseInt(prev.name.split('_')[1]);
            let n2 = parseInt(next.name.split('_')[1]);
            return n1 - n2
        });

        building.children.forEach((floor, index) => {
            floor.children = floor.children.sort((prev, next) => {
                if (prev.name.startsWith(config.resources.bundle.selectionBoxes.floorPlugName)) {
                    return 1
                } else {
                    let n1 = parseInt(prev.name.split('_')[2]);
                    let n2 = parseInt(next.name.split('_')[2]);
                    return n1 - n2
                }
            });

            const floorNumber = Number(floor.name.split('_')[1]);
            floor.userData.base_scale = new Vector3(floor.scale.x, floor.scale.y, floor.scale.z);
            floor.userData.basePosition = new Vector3(floor.position.x, floor.position.y, floor.position.z);
            floor.userData.baseWorldPosition = floor.getWorldPosition(new Vector3());
            if (floor.children.length) {
                const floorArr = [];
                floor.children.forEach((apt, index) => {
                    apt.layers.enable(1);
                    if (!apt.name.startsWith(config.resources.bundle.selectionBoxes.floorPlugName)) {
                        const existProp = dataProps.find(prop => prop.engineId === apt.name);
                        if (existProp) {
                            setDataToApartment(apt, existProp, index);
                            createMaterialAndSetBaseColor(apt);
                            info && buildingClass.apartments.push(apt);
                        } else {
                            const amenity = apt.name.split('_').some(word => word === 'amenity');
                            if (!amenity)
                                console.log(`%cCRM data for ${apt.name} is absent`, 'color: orange');
                            setHardcodeDataToApartment(apt, floorNumber);
                            createMaterialAndSetBaseColor(apt);
                        }
                    } else {
                        setAttributeToPlug(apt, floorNumber);
                        setDataToPlug(apt, floorNumber);
                        if (info){
                            buildingClass.floorsPlug.push({floor:floorNumber, data: apt});
                        }
                    }

                    if (apt.name.match(/_amenity_/g)) {
                      createMaterialAndSetBaseColor(apt);
                      setDataToAmenities(apt, index);
                      if (apt.userData.crm_data)
                        buildingClass.customSelections.push(apt);
                    }

                    floorArr.push(apt);

                    // threeJsVariables.slicer.add(apt);
                });

                // Reverse array if first element is CAP
                if (floorArr[0].name.startsWith(config.resources.bundle.selectionBoxes.floorPlugName)) {
                    // const reverse = floorArr.reverse();
                    if (info){
                        buildingClass.addSelection({floor: floorNumber, data: floorArr});
                    }
                } else {
                    if (info){
                        buildingClass.addSelection({floor: floorNumber, data: floorArr});
                    }
                }

                if (threeJsVariables.slicer.active) {
                    buildingClass.addFloor({floor: floorNumber,data: floorArr})
                }

            } else {

                // floor Amenities
                createMaterialAndSetBaseColor(floor);
                setDataToAmenities(floor, index);

              if (floor.name.match(/_amenity_/g) && floor.userData.crm_data) {
                  buildingClass.customSelections.push(floor);
                }
            }
        });

        buildingClass.addInfo(info);

        if (threeJsVariables.slicer.active) {
            buildingClass.floorSliderNumbers = buildingClass.floors.map(el => el.floor).sort((a,b) => b - a);
        }

        buildingClass.selectionsGroup = building;

        addAmenitiesWithoutEngineId(buildingClass);
        threeJsVariables.orbitBuildingsNavigation.addBuilding(buildingClass);
    });
    threeJsVariables.orbitBuildingsNavigation.updateActiveButton();
    threeJsVariables.cloth.setup();
    checkHighlightsAndCrmDataAreInPlace(dataProps);
    Buildings.sortBuildings();
}

export function setHardcodeDataToApartment(apt, count) {
    apt.userData.floor = count - 1;
    apt.userData.base_position = {
        x: apt.position.x,
        y: apt.position.y,
        z: apt.position.z,
    };

}

function generateBuildingInfo (building,data) {
    const buildingEngineId = building.name.split('_')[0];
    const prop = data.find(apt => apt.buildingEngineId === buildingEngineId);
    
    const buildingInfo = {
        "buildingId": prop.bmbyPropID.toString(),
        "buildingEngineId": buildingEngineId,
        "buildingName": prop.buildingName,
        "buildingLabel": prop.buildingName,
        "buildingColor": "#ffffff",
        "buildingBlock": "",
        "buildingLot": "",
        "buildingStage": "A",
        "buildingIcon": "",
        "buildingTitle": []
    };
    
    config.languages.forEach(language => {
        buildingInfo.buildingTitle.push({
            lang: language,
            value: prop.buildingName
        });
    });

    return buildingInfo;
}

export function createBuildingInfo(building, data, settings) {
    const buildingEngineId = building.name.split('_')[0];
    let getBuildingInfo = settings.data.buildings.find(buildingInfo => buildingEngineId === buildingInfo.buildingEngineId);

    if (!getBuildingInfo) {
        getBuildingInfo = generateBuildingInfo(building,data);
        config.buildings.push(getBuildingInfo);
    }

    let towerName = [];

    if (Array.isArray(getBuildingInfo.buildingTitle) && getBuildingInfo.buildingTitle.length){
        getBuildingInfo.buildingTitle.forEach(title => {
           if (!title.value.length){
               title.value = getBuildingInfo.buildingName;
           }
        });

        towerName = getBuildingInfo.buildingTitle;
    } else {
        towerName = getBuildingInfo.buildingName;
    }

    if (getBuildingInfo) {
        const crm_data = data && data.filter(prop => prop.engineId.split('_')[0] === getBuildingInfo.buildingEngineId);
        const info = {
            // towerName: getBuildingInfo.buildingName,
            towerName: towerName,
            labelName: getBuildingInfo.buildingLabel,
            buildingIcon: getBuildingInfo.buildingIcon,
            color: getBuildingInfo.buildingColor,
            buildingLot: getBuildingInfo.buildingLot,
            buildingName: getBuildingInfo.buildingName || buildingEngineId,
            buildingStage: getBuildingInfo.buildingStage,
            // isActive: false,
            // mouseHover: false,
            // name: getBuildingInfo.buildingEngineId,
            // objects_to_opacity: [],
            // objects_to_appear: [],
            // all_apartments: [],
            // floor_obj: {},
            // flooring_obj: {},
            searchResult: [],
            // customSelections: [],
            crm_data,
            availableApartments: crm_data && crm_data.filter(apt => apt.status === 'Available').length
        };

        return info;
    }

}

export function setAttributeToPlug(plug, floor) {
    plug.material.transparent = true;
    plug.material.opacity = 0;
    plug.visible = false;
}

export function setDataToApartment(apartment, data,index) {
    parseFacing(data);
    parseTenant(data);
    data.hasHighlight = true;

    if (settingsData.isConferenceHost) {
        data.assets.sample360 = 'https://dreamsimages.bmby.com/AuraMegadim01/panocontent/tours/test_tour/index.htm';
        if (data.salePrice === 0) {
            const meterCoast = 33000;
            data.salePrice = Math.round(meterCoast * data.totalSpace);
        }

        if (data.floorNum % 2 === 0) {
            data.status = (index + data.floorNum) % 3 === 0 ? 'Available' : 'Sold';
        }
    }

    if (!data.assets?.plan2D && settingsData.isConferenceHost) {
        const plans  = [
            'https://dreamsimages.bmby.com/AuraMegadim01/content2d/3A.png',
            'https://dreamsimages.bmby.com/AuraMegadim01/content2d/4A.png',
            'https://dreamsimages.bmby.com/AuraMegadim01/content2d/3B.png',
            'https://dreamsimages.bmby.com/AuraMegadim01/content2d/5A.png',
            'https://dreamsimages.bmby.com/AuraMegadim01/content2d/4B.png',
            'https://dreamsimages.bmby.com/AuraMegadim01/content2d/5B.png'
        ];
        const selectedPlan = plans[index];

        if (selectedPlan) {
            data.assets.plan2D = selectedPlan;
        } else {
            data.assets.plan2D = plans[Math.round(Math.random() * (plans.length - 1))];
        }
    }

    if (Vantages.active) {
        data.assets.vantage = '';
    }


    apartment.userData.crm_data = data;
    const worldPosition = apartment.getWorldPosition(new Vector3());
    apartment.userData.baseWorldPosition = worldPosition;
    apartment.userData.base_position = {
        x: apartment.position.x,
        y: apartment.position.y,
        z: apartment.position.z,
    };

    // apartment.userData.floor = data.floorNum - config.resources.selectionBoxes.floorNumberCorrection;
    apartment.userData.floor = data.floorNum;
    apartment.userData.type = SelectedObjectType.SelectionBoxes;
    apartment.userData.isActive = false;
    apartment.userData.isClicked = false;

    return apartment;
}

export function parseFacing (data) {
    if (data.facing){
        const  facing = data.facing.split(/,|-/);
        data.facing = [...new Set(facing)];
    } else {
        data.facing = [];
    }
}

function parseTenant(data) {
    if (data.status !== AVAILABLE_STATUS)
        return;

    const isTenant = config.features?.isTenant;
    if (!isTenant)
        return;

    switch (parseInt(data?.tenantPrice)) {
        case 0:
            data.status = isTenant['0']
            break;
        case 1:
            data.status = isTenant['1']
            break;
    }
}

export function setDataToPlug(plug, floorNumber) {
    const worldPosition = plug.getWorldPosition(new Vector3());
    plug.userData.baseWorldPosition = worldPosition;
    plug.userData.base_position = {
        x: plug.position.x,
        y: plug.position.y,
        z: plug.position.z,
    };
    plug.userData.floor = floorNumber;
    plug.userData.type = SelectedObjectType.Plug;
    plug.userData.customSelection = true;
}

const dataOfAmenities = (floor) => {
    if ((!config.features.amenities || config.features.amenities.length <= 0) && !settingsData.amenities) return;

    const amenityData = settingsData.amenities || config.features.amenities;
    if (floor.name.match(/_amenity_/g)) {
        const amenity = amenityData.find(amen => amen?.engineId === floor.name);
        if (amenity) {
            floor.userData.floor = amenity.floor;
            floor.userData.crm_data = config.features.amenities.find(amen => amen.engineId === floor.name);
        }
    }
}

export function setDataToAmenities(amenities, index) {
    amenities.userData.type = SelectedObjectType.SelectionBoxes;
    amenities.userData.customSelection = true;
    dataOfAmenities(amenities, index);
}

export function setProjectSettings(settingsFromCrm, dictionary) {
    if (settingsFromCrm && settingsFromCrm.data) {
        const settings = settingsFromCrm.data;
        settings.languages = filterDisabledLanguages(settings.languages);
        config['crmProjectSettings'] = settings;

        // remove the line after API is removed
        delete config.crmProjectSettings?.contactWidget;

        for (const key in settings) {
            config[key] = settings[key];
        }
    }

    if (dictionary && dictionary.data) {
        config.dictionary = dictionary.data;
    }
}

export function checkHighlightsAndCrmDataAreInPlace(aptData) {
    const aptsWithoutSelectionBoxes = [];
    settingsData.allCrmData = aptData.filter((apt) => {
        if (!apt?.hasHighlight) {
            aptsWithoutSelectionBoxes.push(apt.propNum);
            return false;
        }
        return true;
    });

    if (aptsWithoutSelectionBoxes.length > 0)
        sendLogs(`propNums: ${aptsWithoutSelectionBoxes.join(',')}`, 'error');
}
